import { motion } from "framer-motion";
import { Check, Sun, Wind, Cpu, Home, Wheat, Droplets } from "lucide-react";
import { useLanguage } from "../i18n/translations";

const SOLAR_IMG = "https://images.unsplash.com/photo-1780922577018-f49a4223e2d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHBhbmVsJTIwdGVjaG5pY2lhbiUyMGluc3RhbGxhdGlvbiUyMHNvbGFyJTIwZmFybXxlbnwwfHx8fDE3OTE4MTIzMTV8MA&ixlib=rb-4.1.0&q=85";
const WIND_IMG = "https://images.unsplash.com/photo-1452179535021-368bb0edc3a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHx3aW5kJTIwdHVyYmluZXMlMjBncmVlbiUyMGVuZXJneSUyMGZpZWxkfGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85";
const ROOF_IMG = "https://images.unsplash.com/photo-1771479755134-9c1e3143c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHMlMjBob21lJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.65, delay },
});

export const Services = () => {
  const { t } = useLanguage();
  const specIcons = [Home, Wheat, Droplets];

  return (
    <section id="services" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div {...reveal()} className="mb-14 max-w-2xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-eco-bright">{t.services.eyebrow}</p>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-12">
          <motion.div
            {...reveal(0.05)}
            className="group relative overflow-hidden rounded-3xl border border-white/10 md:col-span-7 md:min-h-[340px]"
            data-testid="bento-solar-card"
          >
            <img src={SOLAR_IMG} alt="Solar installation" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/20" />
            <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-solar/20 backdrop-blur">
                <Sun size={22} className="text-solar-yellow" />
              </div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{t.services.solar.t}</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">{t.services.solar.d}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {t.services.solar.chips.map((c) => (
                  <span key={c} className="rounded-full border border-solar/40 bg-solar/10 px-3 py-1 text-xs font-semibold text-solar-highlight">{c}</span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            {...reveal(0.15)}
            className="group relative overflow-hidden rounded-3xl border border-white/10 md:col-span-5 md:min-h-[340px]"
            data-testid="bento-wind-card"
          >
            <img src={WIND_IMG} alt="Wind farm" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/20" />
            <div className="relative flex h-full flex-col justify-end p-7">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-wind/20 backdrop-blur">
                <Wind size={22} className="text-wind" />
              </div>
              <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{t.services.wind.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{t.services.wind.d}</p>
            </div>
          </motion.div>

          <motion.div
            {...reveal(0.1)}
            className="rounded-3xl border border-white/10 bg-navy-900 p-7 transition-colors duration-300 hover:border-eco/40 sm:p-9 md:col-span-12"
            data-testid="bento-ai-card"
          >
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-eco/15">
                  <Cpu size={22} className="text-eco-bright" />
                </div>
                <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{t.services.ai.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.services.ai.d}</p>
              </div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {t.services.ai.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-navy-950/60 px-4 py-3 text-sm font-medium text-slate-200">
                    <Check size={15} className="shrink-0 text-eco-bright" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="mt-24">
          <motion.div {...reveal()} className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-solar-highlight">{t.spec.eyebrow}</p>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">{t.spec.title}</h2>
          </motion.div>

          <div className="grid gap-5 md:grid-cols-3">
            {t.spec.items.map((item, i) => {
              const Icon = specIcons[i];
              const withImage = i === 0;
              return (
                <motion.div
                  key={item.t}
                  {...reveal(i * 0.1)}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-navy-900 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-solar/40 hover:shadow-[0_20px_60px_rgba(245,158,11,0.12)]"
                  data-testid={`spec-card-${i + 1}`}
                >
                  {withImage && (
                    <>
                      <img src={ROOF_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/80 to-navy-900/40" />
                    </>
                  )}
                  <div className="relative">
                    <div className="mb-5 flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-solar/15 transition-colors duration-300 group-hover:bg-solar/25">
                        <Icon size={24} className="text-solar-yellow" />
                      </div>
                      <span className="font-mono text-3xl font-black text-white/10 transition-colors duration-300 group-hover:text-solar/30">0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-white">{item.t}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{item.d}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
